import type { UsuarioPublic } from '@app/shared';
import { useMutation } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { usuariosApi } from '../api/usuarios.api';

interface Props {
  usuario: UsuarioPublic;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UsuarioResetPasswordDialog({ usuario, open, onOpenChange }: Props): JSX.Element {
  const [passwordTemporal, setPasswordTemporal] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const es = useI18n();

  const mutation = useMutation({
    mutationFn: () => usuariosApi.resetPassword(usuario.id),
    onSuccess: ({ passwordTemporal: pw }) => {
      setPasswordTemporal(pw);
      toast.success(es.admin.usuarios.reset_dialog.exito);
    },
    onError: (error) => {
      setGlobalError(getErrorMessage(error, es.errores.generico.algoSalioMal));
    },
  });

  function handleClose(): void {
    setPasswordTemporal(null);
    setGlobalError(null);
    onOpenChange(false);
  }

  async function copyPassword(): Promise<void> {
    if (passwordTemporal) {
      await navigator.clipboard.writeText(passwordTemporal);
      toast.success(es.admin.usuarios.reset_dialog.copiada);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{es.admin.usuarios.reset_dialog.titulo}</DialogTitle>
          <DialogDescription>{usuario.email}</DialogDescription>
        </DialogHeader>

        {passwordTemporal ? (
          <div className="flex flex-col gap-4">
            <Alert variant="success" title={es.admin.usuarios.reset_dialog.generadaTitulo}>
              {es.admin.usuarios.reset_dialog.generadaDescripcion}
            </Alert>
            <div className="flex items-center gap-2 rounded-md border border-marca-neutro bg-marca-neutro/30 p-3">
              <code className="flex-1 break-all font-mono text-sm text-marca-gris">{passwordTemporal}</code>
              <Button type="button" variant="outline" size="sm" onClick={() => void copyPassword()}>
                <Copy size={14} />
                {es.admin.usuarios.reset_dialog.copiar}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>{es.admin.usuarios.reset_dialog.cerrar}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Alert variant="info">{es.admin.usuarios.reset_dialog.advertencia}</Alert>
            {globalError && <Alert variant="error">{globalError}</Alert>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                {es.admin.usuarios.reset_dialog.cancelar}
              </Button>
              <Button variant="danger" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                {mutation.isPending
                  ? es.admin.usuarios.reset_dialog.reseteando
                  : es.admin.usuarios.reset_dialog.resetear}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
